/**
 * Main Menu Scene - Redesigned
 *
 * Features:
 * - Zone gradient background (all 6 zones visible)
 * - Tilt-reactive parallax effect
 * - Floating zone-themed particles
 * - Animated Blobo character
 * - Glassmorphism UI buttons
 * - High DPI text support
 */

import Phaser from 'phaser';
import { MenuParticleSystem } from '../ui/effects/MenuParticleSystem';
import { MenuBlobo } from '../ui/components/MenuBlobo';
import { GlassButton } from '../ui/components/GlassButton';
import { TYPOGRAPHY, Z_INDEX } from '../config/designSystem';
import { ICONS } from '../config/icons';
import { OrientationPermissionManager } from '../utils/OrientationPermissionManager';
import packageJson from '../../package.json';

export class MainMenuScene extends Phaser.Scene {
  private particles!: MenuParticleSystem;
  private blobo!: MenuBlobo;
  private tiltAngle: number = 0;
  private installButton?: GlassButton;
  private playButton?: GlassButton;
  private leaderboardButton?: GlassButton;
  private settingsButton?: Phaser.GameObjects.Image;
  private infoButton?: Phaser.GameObjects.Text;
  private continueButton?: Phaser.GameObjects.Text;
  private menuRevealed: boolean = false;
  private continueButtonPointerDown: boolean = false; // Track if press started on continue button

  // Session storage key for "Continue in browser" selection
  private readonly SESSION_KEY_CONTINUE_IN_BROWSER = 'blobo_continue_in_browser';

  constructor() {
    super({ key: 'MainMenuScene' });
  }

  create(): void {
    // Reset state when scene is restarted
    this.menuRevealed = false;
    this.continueButtonPointerDown = false;

    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    const height = this.cameras.main.height;

    // Simple dark background instead of zone gradient
    this.cameras.main.setBackgroundColor('#0A0E27');

    // Create floating particles for atmosphere
    this.particles = new MenuParticleSystem(this);

    // Set up tilt system to feed events to particles and Blobo
    this.setupTiltSystem();

    // Create animated Blobo character (larger to match game proportions)
    const bloboY = Math.min(centerY - 120, height * 0.35);
    this.blobo = new MenuBlobo(this, centerX, bloboY, 80);
    this.blobo.setDepth(Z_INDEX.uiContent);

    // Title removed - Blobo character is sufficient branding

    // Buttons - using GlassButton components
    const buttonStartY = Math.max(centerY + 80, bloboY + 250);
    this.createButtons(centerX, buttonStartY);

    // Version info
    this.createVersionInfo();

    // Check if user already selected "Continue in browser" this session
    // If yes, automatically reveal menu (skip install prompt)
    if (this.hasSelectedContinueInBrowser()) {
      this.revealMenu();
    }
  }

  /**
   * Create glass-style buttons
   */
  private createButtons(centerX: number, startY: number): void {
    let currentY = startY;

    // Check if we should show install button - comprehensive detection
    const isStandalone = this.isRunningAsApp();

    // Install button (green) - show only if NOT running as installed app
    if (!isStandalone) {
      this.installButton = new GlassButton(this, centerX, currentY, {
        text: 'INSTALL APP',
        iconKey: ICONS.INSTALL_MOBILE,
        fontSize: 22,
        width: 260,
        height: 56,
        style: 'secondary', // We'll customize the color
        onClick: () => this.handleInstall(),
      });
      // Change to green color
      (this.installButton as any).bg.setFillStyle(0x10b981, 0.9); // Green
      this.installButton.setDepth(Z_INDEX.uiContent);
      currentY += 70;
    }

    // Play button (primary - gold) - show immediately if running as app
    this.playButton = new GlassButton(this, centerX, currentY, {
      text: 'PLAY',
      iconKey: ICONS.PLAY_CIRCLE,
      fontSize: 28,
      width: 260,
      height: 64,
      style: 'primary',
      onClick: () => this.startGame(),
    });
    this.playButton.setDepth(Z_INDEX.uiContent);
    this.playButton.setVisible(isStandalone); // Show if running as app

    // Leaderboard button (secondary - centered below play) - show immediately if running as app
    this.leaderboardButton = new GlassButton(this, centerX, currentY + 85, {
      text: 'LEADERBOARD',
      iconKey: ICONS.TROPHY,
      fontSize: 20,
      width: 200,
      height: 52,
      style: 'secondary',
      onClick: () => this.openLeaderboard(),
    });
    this.leaderboardButton.setDepth(Z_INDEX.uiContent);
    this.leaderboardButton.setVisible(isStandalone); // Show if running as app

    // Settings button (small icon in bottom right corner) - no background
    const height = this.cameras.main.height;
    const width = this.cameras.main.width;
    const iconSize = 32;
    this.settingsButton = this.add.image(width - 30, height - 30, ICONS.SETTINGS);
    this.settingsButton.setDisplaySize(iconSize, iconSize);
    this.settingsButton.setTint(0xffffff);
    this.settingsButton.setInteractive({ useHandCursor: true });
    this.settingsButton.on('pointerdown', () => this.openSettings());
    this.settingsButton.on('pointerover', () => {
      this.settingsButton!.setTint(0xffd700); // Gold on hover
      this.blobo.jump();
    });
    this.settingsButton.on('pointerout', () => {
      this.settingsButton!.setTint(0xffffff); // White when not hovering
    });
    this.settingsButton.setDepth(Z_INDEX.uiContent);
    this.settingsButton.setVisible(isStandalone); // Show if running as app

    // Continue in browser button (text only) - only show when NOT running as app
    if (!isStandalone) {
      this.continueButton = this.add.text(centerX, currentY + 30, 'Continue in browser', {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.body}px`,
        color: '#9CA3AF',
        resolution: Math.max(4, window.devicePixelRatio * 2),
      });
      this.continueButton.setOrigin(0.5);
      this.continueButton.setDepth(Z_INDEX.uiContent);
      this.continueButton.setInteractive({ useHandCursor: true });

      this.continueButton.on('pointerover', () => {
        this.continueButton?.setColor('#FFFFFF');
      });

      this.continueButton.on('pointerout', () => {
        this.continueButton?.setColor('#9CA3AF');
        // Reset flag if user drags away from button
        this.continueButtonPointerDown = false;
      });

      this.continueButton.on('pointerdown', () => {
        // Mark that press started on this button
        this.continueButtonPointerDown = true;
      });

      this.continueButton.on('pointerup', () => {
        // Only reveal menu if press started on this button (prevents swipe false positives)
        if (this.continueButtonPointerDown) {
          this.revealMenu();
        }
        // Reset flag after handling
        this.continueButtonPointerDown = false;
      });
    }

    // Button hover interaction with Blobo
    this.playButton.on('pointerover', () => {
      this.blobo.jump();
    });
    this.leaderboardButton.on('pointerover', () => {
      this.blobo.jump();
    });
    // Settings button hover already handled in creation
  }

  /**
   * Check if user has selected "Continue in browser" this session
   */
  private hasSelectedContinueInBrowser(): boolean {
    try {
      return sessionStorage.getItem(this.SESSION_KEY_CONTINUE_IN_BROWSER) === 'true';
    } catch {
      // sessionStorage might not be available in some contexts
      return false;
    }
  }

  /**
   * Mark that user selected "Continue in browser" for this session
   */
  private markContinueInBrowser(): void {
    try {
      sessionStorage.setItem(this.SESSION_KEY_CONTINUE_IN_BROWSER, 'true');
    } catch {
      // sessionStorage might not be available, fail silently
      console.warn('⚠️ Could not save continue-in-browser preference to sessionStorage');
    }
  }

  /**
   * Reveal the main menu buttons when user wants to continue in browser
   */
  private revealMenu(): void {
    if (this.menuRevealed) return;

    this.menuRevealed = true;

    // Save user's choice for this session
    this.markContinueInBrowser();

    // Hide the continue button
    this.continueButton?.setVisible(false);

    // Show and animate in the main buttons
    this.playButton?.setVisible(true);
    this.leaderboardButton?.setVisible(true);
    this.settingsButton?.setVisible(true); // Settings button already visible in corner

    // Animate buttons in with a stagger
    if (this.playButton) {
      this.playButton.setAlpha(0);
      this.tweens.add({
        targets: this.playButton,
        alpha: 1,
        duration: 300,
        ease: 'Power2',
      });
    }

    if (this.leaderboardButton) {
      this.leaderboardButton.setAlpha(0);
      this.tweens.add({
        targets: this.leaderboardButton,
        alpha: 1,
        delay: 100,
        duration: 300,
        ease: 'Power2',
      });
    }

    // Settings button and info button in corner don't need fade-in animation (always visible)

    // Make Blobo jump
    this.blobo.jump();
  }

  /**
   * Comprehensive check if app is running as installed PWA
   */
  private isRunningAsApp(): boolean {
    // Check 1: Display mode (most reliable for modern browsers)
    const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isDisplayModeFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const isDisplayModeMinimalUi = window.matchMedia('(display-mode: minimal-ui)').matches;

    // Check 2: iOS standalone mode
    const isIOSStandalone = (window.navigator as any).standalone === true;

    // App is considered "running as installed" if ANY of these are true
    const isStandalone =
      isDisplayModeStandalone ||
      isDisplayModeFullscreen ||
      isDisplayModeMinimalUi ||
      isIOSStandalone;

    return isStandalone;
  }

  /**
   * Create version info and info button
   */
  private createVersionInfo(): void {
    // Version text at bottom left
    const version = this.add.text(
      10,
      this.cameras.main.height - 10,
      `v${packageJson.version}`, // Automatically read from package.json
      {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
        color: '#9CA3AF',
        stroke: '#000000',
        strokeThickness: 2,
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      }
    );
    version.setOrigin(0, 1);
    version.setDepth(Z_INDEX.uiContent);
    version.setScrollFactor(0);

    // Info button (icon only, no background) above version - always visible
    this.infoButton = this.add.text(30, this.cameras.main.height - 48, 'ℹ', {
      fontSize: '20px',
      color: '#9CA3AF',
      resolution: Math.max(2, window.devicePixelRatio) * 2,
    });
    this.infoButton.setOrigin(0.5);
    this.infoButton.setDepth(Z_INDEX.uiContent);
    this.infoButton.setScrollFactor(0);
    this.infoButton.setInteractive({ useHandCursor: true });

    // Hover effects
    this.infoButton.on('pointerover', () => {
      this.infoButton?.setScale(1.1);
      this.infoButton?.setColor('#FFFFFF');
      this.blobo.jump();
    });

    this.infoButton.on('pointerout', () => {
      this.infoButton?.setScale(1);
      this.infoButton?.setColor('#9CA3AF');
    });

    this.infoButton.on('pointerdown', () => {
      this.infoButton?.setScale(0.95);
    });

    this.infoButton.on('pointerup', () => {
      this.infoButton?.setScale(1);
      this.openImprint();
    });
  }

  /**
   * Set up tilt system to emit events for particles and Blobo
   */
  private setupTiltSystem(): void {
    // Listen for device orientation
    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', this.handleDeviceOrientation.bind(this));
    }

    // Keyboard simulation for testing
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        this.tiltAngle = Math.max(this.tiltAngle - 2, -15);
        this.emitTiltUpdate();
      } else if (event.key === 'ArrowRight') {
        this.tiltAngle = Math.min(this.tiltAngle + 2, 15);
        this.emitTiltUpdate();
      }
    });
  }

  /**
   * Handle device orientation events
   */
  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    if (event.gamma !== null) {
      // Use gamma (left/right tilt)
      // Clamp to ±15 degrees
      this.tiltAngle = Phaser.Math.Clamp(event.gamma, -15, 15);
      this.emitTiltUpdate();
    }
  }

  /**
   * Emit tilt update event for particles and Blobo googly eyes
   */
  private emitTiltUpdate(): void {
    this.events.emit('tilt-update', this.tiltAngle);

    // Update Blobo's pupils based on tilt (googly eyes effect)
    if (this.blobo) {
      this.blobo.updatePupilsFromTilt(this.tiltAngle);
    }
  }

  /**
   * Start game (navigate to level selection)
   */
  private async startGame(): Promise<void> {
    // Request device orientation permission if needed (iOS 13+)
    // Uses sessionStorage to avoid asking multiple times per session
    if (
      OrientationPermissionManager.requiresPermission() &&
      !OrientationPermissionManager.isGrantedThisSession()
    ) {
      const granted = await OrientationPermissionManager.requestPermission();

      if (!granted) {
        console.warn('⚠️ Permission denied - launching game without tilt controls');
      }
    }

    // Launch game regardless (tilt controls will be disabled if permission denied)
    this.launchLevelSelect();
  }

  /**
   * Launch level select scene
   */
  private launchLevelSelect(): void {
    // Fade out transition
    this.cameras.main.fadeOut(300, 0, 0, 0);

    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.cleanup();
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * Open settings
   */
  private openSettings(): void {
    this.cameras.main.fadeOut(300, 0, 0, 0);

    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.cleanup();
      this.scene.start('SettingsScene');
    });
  }

  /**
   * Open leaderboard
   */
  private openLeaderboard(): void {
    this.cameras.main.fadeOut(300, 0, 0, 0);

    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.cleanup();
      this.scene.start('LeaderboardScene');
    });
  }

  /**
   * Open imprint
   */
  private openImprint(): void {
    this.cameras.main.fadeOut(300, 0, 0, 0);

    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.cleanup();
      this.scene.start('ImprintScene');
    });
  }

  /**
   * Handle PWA install button click
   */
  private async handleInstall(): Promise<void> {
    // Make Blobo jump
    this.blobo.jump();

    // Always show InstallPromptScene for consistent UX
    // The scene will handle browser-specific installation (native prompt, instructions, etc.)
    this.scene.launch('InstallPromptScene');
    this.scene.bringToTop('InstallPromptScene');
  }

  /**
   * Cleanup on scene shutdown
   */
  private cleanup(): void {
    // Remove device orientation listener
    window.removeEventListener('deviceorientation', this.handleDeviceOrientation.bind(this));

    // Destroy UI systems
    if (this.particles) {
      this.particles.destroy();
    }
  }

  /**
   * Scene shutdown
   */
  shutdown(): void {
    this.cleanup();
  }
}
